import { useLocalSearchParams } from 'expo-router';
import { useCallback, useRef } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { BackButton } from '../../../../../src/components/primitives/BackButton';
import { Badge } from '../../../../../src/components/primitives/Badge';
import { HStack, VStack } from '../../../../../src/components/primitives/Stack';
import {
  Body,
  Caption,
  Heading2,
  Heading4,
  Label,
} from '../../../../../src/components/primitives/Text';
import { useAuth } from '../../../../../src/contexts/AuthContext';
import { useTranslation } from '../../../../../src/contexts/TranslationContext';
import {
  AudioPlayer,
  type AudioPlayerHandle,
} from '../../../../../src/features/voice-agents/components/AudioPlayer';
import {
  useVoiceCallAudio,
  useVoiceCallReplay,
} from '../../../../../src/features/voice-agents/hooks';
import type { ReplayTurn } from '../../../../../src/features/voice-agents/types';
import { colors } from '../../../../../src/theme/colors';

const SIDE_PANEL_BREAKPOINT = 900;

function formatMs(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function ToolCallCard({ tc }: { tc: NonNullable<ReplayTurn['toolCall']> }) {
  const { t } = useTranslation();
  const latencyLabel = tc.latencyMs != null ? `${tc.latencyMs}ms` : '—';
  const statusColor =
    tc.status === 'completed' ? colors.status.success.main : colors.status.error.main;

  return (
    <View
      style={styles.toolCard}
      accessible
      accessibilityRole="text"
      accessibilityLabel={`${t('voiceAgents.replay.toolPrefix')} ${tc.toolName}, ${t('voiceAgents.replay.latencyLabel')} ${latencyLabel}, ${t('voiceAgents.replay.statusLabel')} ${tc.status}`}
    >
      <HStack spacing="sm" style={styles.toolCardHeader}>
        <Label style={styles.toolName} numberOfLines={1}>
          {tc.toolName}
        </Label>
        <Caption style={[styles.toolStatus, { color: statusColor }]}>{tc.status}</Caption>
        <Caption style={styles.toolLatency}>{latencyLabel}</Caption>
      </HStack>
      {tc.errorMessage ? <Caption style={styles.toolError}>{tc.errorMessage}</Caption> : null}
    </View>
  );
}

function TranscriptBubbleTurn({
  turn,
  entry,
  onSeek,
}: {
  turn: ReplayTurn;
  entry: NonNullable<ReplayTurn['transcript']>;
  onSeek: (ms: number) => void;
}) {
  const { t } = useTranslation();
  const isAgent = entry.speaker === 'agent';
  const tsLabel = entry.startedAtMs != null ? formatMs(entry.startedAtMs) : '';
  const speakerLabel = isAgent ? t('voiceAgents.replay.agent') : t('voiceAgents.replay.lead');
  const handlePress = useCallback(() => {
    if (entry.startedAtMs != null) onSeek(entry.startedAtMs);
  }, [entry.startedAtMs, onSeek]);

  return (
    <Pressable
      onPress={handlePress}
      style={({ pressed }) => [
        styles.bubbleWrapper,
        isAgent ? styles.agentWrapper : styles.leadWrapper,
        pressed && styles.bubblePressed,
      ]}
      hitSlop={{ top: 6, bottom: 6, left: 8, right: 8 }}
      accessibilityRole="button"
      accessibilityLabel={`${t('voiceAgents.replay.turnPrefix')} ${turn.turnIndex}, ${speakerLabel} ${t('voiceAgents.replay.atTime')} ${tsLabel}: ${entry.text}. ${t('voiceAgents.replay.tapToSeek')}`}
      accessibilityHint={t('voiceAgents.replay.seekHint')}
    >
      <Caption
        style={[styles.speakerLabel, isAgent ? styles.agentLabel : styles.leadLabel]}
        accessibilityElementsHidden
        importantForAccessibility="no"
      >
        {speakerLabel}
        {tsLabel ? `  ${tsLabel}` : ''}
      </Caption>
      <View style={[styles.bubble, isAgent ? styles.agentBubble : styles.leadBubble]}>
        <Body style={[styles.bubbleText, isAgent ? styles.agentText : styles.leadText]}>
          {entry.text}
        </Body>
      </View>
    </Pressable>
  );
}

function TurnRow({ turn, onSeek }: { turn: ReplayTurn; onSeek: (ms: number) => void }) {
  const { t } = useTranslation();
  return (
    <VStack spacing="sm" style={styles.turnRow}>
      <HStack spacing="sm" style={styles.turnIndexRow}>
        <View
          style={styles.turnIndexBadge}
          accessibilityElementsHidden
          importantForAccessibility="no"
        >
          <Caption style={styles.turnIndexText}>{turn.turnIndex}</Caption>
        </View>
      </HStack>
      {turn.transcript ? (
        <TranscriptBubbleTurn turn={turn} entry={turn.transcript} onSeek={onSeek} />
      ) : null}
      {turn.toolCall ? (
        <View
          style={styles.inlineToolHint}
          accessible
          accessibilityLabel={t('voiceAgents.replay.toolExecuted')}
        >
          <Caption style={styles.inlineToolText}>
            ⚙ {turn.toolCall.toolName} ({turn.toolCall.latencyMs ?? '—'}ms)
          </Caption>
        </View>
      ) : null}
    </VStack>
  );
}

function ToolCallsSidePanel({ turns }: { turns: ReplayTurn[] }) {
  const { t } = useTranslation();
  const toolCalls = turns.flatMap((turn) =>
    turn.toolCall ? [{ turnIndex: turn.turnIndex, toolCall: turn.toolCall }] : [],
  );

  return (
    <View
      style={styles.sidePanel}
      accessibilityLabel={`${t('voiceAgents.replay.toolsPanelPrefix')} ${toolCalls.length} ${t('voiceAgents.replay.callsLabel')}`}
      {...(Platform.OS === 'web' ? ({ role: 'region' } as object) : {})}
    >
      <Heading4 style={styles.sidePanelTitle} accessibilityRole="header">
        {t('voiceAgents.replay.tools')}
      </Heading4>
      {toolCalls.length === 0 ? (
        <Caption style={styles.emptyText}>{t('voiceAgents.replay.noToolCalls')}</Caption>
      ) : (
        <VStack spacing="sm">
          {toolCalls.map((tc) => (
            <View key={`tc-${tc.turnIndex}`}>
              <Caption style={styles.toolTurnLabel}>
                {t('voiceAgents.replay.turnLabel')} {tc.turnIndex}
              </Caption>
              <ToolCallCard tc={tc.toolCall} />
            </View>
          ))}
        </VStack>
      )}
    </View>
  );
}

export default function CallReplayScreen() {
  const { t } = useTranslation();
  const { callId } = useLocalSearchParams<{ callId: string }>();
  const { currentOrganization, backendUser } = useAuth();
  const orgId = currentOrganization?.id ?? '';
  const audioRef = useRef<AudioPlayerHandle>(null);

  const isAdmin = backendUser?.role === 'admin' || backendUser?.role === 'manager';

  const { data: replay, isLoading } = useVoiceCallReplay(callId ?? '', orgId);
  const { data: audio } = useVoiceCallAudio(callId ?? '', orgId);

  const { width } = useWindowDimensions();
  const isWide = Platform.OS === 'web' && width >= SIDE_PANEL_BREAKPOINT;

  const seekToMs = useCallback((ms: number) => {
    audioRef.current?.seekTo(ms / 1000);
  }, []);

  if (!isAdmin) {
    return (
      <View
        style={styles.center}
        accessible
        accessibilityLabel={t('voiceAgents.replay.restrictedTitle')}
        accessibilityRole="alert"
        accessibilityLiveRegion="assertive"
      >
        <View style={styles.restrictedCard}>
          <Heading4 style={styles.restrictedTitle} accessibilityRole="header">
            {t('voiceAgents.replay.restrictedTitle')}
          </Heading4>
          <Body style={styles.restrictedBody}>{t('voiceAgents.replay.restrictedBody')}</Body>
        </View>
      </View>
    );
  }

  if (isLoading) {
    return (
      <View
        style={styles.center}
        accessible
        accessibilityLabel={t('voiceAgents.replay.loading')}
        accessibilityRole="progressbar"
      >
        <ActivityIndicator color={colors.primary.main} size="large" />
      </View>
    );
  }

  if (!replay) {
    return (
      <View style={styles.center}>
        <Body style={{ color: colors.text.secondary }}>{t('voiceAgents.replay.unavailable')}</Body>
      </View>
    );
  }

  const audioUrl = audio?.url ?? null;

  const audioBlock = audioUrl ? (
    <AudioPlayer
      ref={audioRef}
      url={audioUrl}
      durationSeconds={replay.call.durationSeconds ?? null}
      testID="replay-audio-player"
    />
  ) : (
    <View
      style={styles.audioMissing}
      accessible
      accessibilityLabel={t('voiceAgents.replay.audioUnavailableA11y')}
    >
      <Caption style={styles.emptyText}>{t('voiceAgents.replay.audioUnavailable')}</Caption>
    </View>
  );

  const turnsBlock =
    replay.turns.length === 0 ? (
      <View
        style={styles.emptyState}
        accessible
        accessibilityLabel={t('voiceAgents.replay.noTurnsA11y')}
      >
        <Caption style={styles.emptyText}>{t('voiceAgents.replay.noReplayData')}</Caption>
      </View>
    ) : (
      <VStack spacing="xs">
        {replay.turns.map((turn) => (
          <TurnRow key={`turn-${turn.turnIndex}`} turn={turn} onSeek={seekToMs} />
        ))}
      </VStack>
    );

  return (
    <ScrollView
      style={styles.root}
      contentContainerStyle={styles.content}
      accessibilityLabel={t('voiceAgents.replay.screenA11y')}
    >
      <HStack style={styles.topBar} spacing="md">
        <BackButton
          accessibilityLabel={t('common.actions.goBack')}
          tooltip={t('common.actions.goBack')}
          fallbackHref={callId ? `/voice-agents/calls/${callId}` : '/voice-agents/calls'}
        />
        <Heading2 style={styles.title} accessibilityRole="header" numberOfLines={1}>
          {t('voiceAgents.replay.title')}
        </Heading2>
        <Badge
          color="cyan"
          size="sm"
          accessibilityLabel={`${replay.turns.length} ${t('voiceAgents.replay.turns')}`}
        >
          {replay.turns.length} {t('voiceAgents.replay.turns')}
        </Badge>
      </HStack>

      {audioBlock}

      <View style={isWide ? styles.wideLayout : styles.stackedLayout}>
        <View style={isWide ? styles.wideMain : undefined}>{turnsBlock}</View>
        <View style={isWide ? styles.wideSide : undefined}>
          <ToolCallsSidePanel turns={replay.turns} />
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    padding: 16,
    paddingBottom: 40,
    gap: 16,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    backgroundColor: colors.background.primary,
  },
  topBar: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    color: colors.text.primary,
    flex: 1,
  },
  wideLayout: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  wideMain: {
    flex: 2,
  },
  wideSide: {
    flex: 1,
    minWidth: 280,
    maxWidth: 360,
  },
  stackedLayout: {
    gap: 16,
  },
  audioMissing: {
    padding: 16,
    backgroundColor: colors.background.elevated,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
  turnRow: {
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderColor: colors.border.muted,
  },
  turnIndexRow: {
    alignItems: 'center',
  },
  turnIndexBadge: {
    backgroundColor: colors.surface.row,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    minWidth: 28,
    alignItems: 'center',
  },
  turnIndexText: {
    color: colors.text.tertiary,
    fontSize: 11,
    fontWeight: '600',
  },
  bubbleWrapper: {
    maxWidth: '85%',
    gap: 4,
  },
  bubblePressed: {
    opacity: 0.7,
  },
  agentWrapper: {
    alignSelf: 'flex-start',
  },
  leadWrapper: {
    alignSelf: 'flex-end',
  },
  speakerLabel: {
    fontSize: 10,
    color: colors.text.tertiary,
  },
  agentLabel: {
    textAlign: 'left',
  },
  leadLabel: {
    textAlign: 'right',
  },
  bubble: {
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  agentBubble: {
    backgroundColor: colors.primary.muted,
    borderBottomLeftRadius: 4,
  },
  leadBubble: {
    backgroundColor: colors.surface.row,
    borderBottomRightRadius: 4,
  },
  bubbleText: {
    fontSize: 14,
    lineHeight: 20,
  },
  agentText: {
    color: colors.primary[200],
  },
  leadText: {
    color: colors.text.primary,
  },
  inlineToolHint: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  inlineToolText: {
    color: colors.text.tertiary,
    fontSize: 11,
  },
  sidePanel: {
    backgroundColor: colors.background.card,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    gap: 12,
  },
  sidePanelTitle: {
    color: colors.text.primary,
    fontWeight: '600',
  },
  toolTurnLabel: {
    color: colors.text.tertiary,
    marginBottom: 4,
    fontSize: 11,
    textTransform: 'uppercase',
  },
  toolCard: {
    backgroundColor: colors.surface.row,
    borderRadius: 8,
    padding: 12,
    gap: 4,
  },
  toolCardHeader: {
    alignItems: 'center',
  },
  toolName: {
    color: colors.text.primary,
    fontWeight: '600',
    fontSize: 13,
    flex: 1,
  },
  toolStatus: {
    fontSize: 11,
    fontWeight: '500',
  },
  toolLatency: {
    color: colors.text.tertiary,
    fontSize: 11,
  },
  toolError: {
    color: colors.status.error.main,
    fontSize: 12,
    marginTop: 4,
  },
  emptyState: {
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    color: colors.text.tertiary,
  },
  restrictedCard: {
    backgroundColor: colors.background.card,
    borderRadius: 16,
    padding: 24,
    gap: 12,
    maxWidth: 360,
    borderWidth: 1,
    borderColor: colors.status.error.main,
  },
  restrictedTitle: {
    color: colors.status.error.main,
    fontWeight: '700',
  },
  restrictedBody: {
    color: colors.text.secondary,
    lineHeight: 22,
  },
});
